import { Text } from '@mantine/core';
import { CurrencyType } from '@dealstack/shared/lib/generated';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { AmountFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/AmountFormatted.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { HoldingsSummaryCardStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsSummaryCard/HoldingsSummaryCard.styles.tsx';
import { HoldingsTotals } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsSummaryCard/holdingsSummary.utils.ts';

interface Props {
	totals: HoldingsTotals;
	currency?: CurrencyType;
	loanCurrency?: CurrencyType;
	isError?: boolean;
	showLoan?: boolean;
}

export const HoldingsSummary = ({
	totals,
	currency,
	loanCurrency,
	isError,
	showLoan = true,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const amount = (value: number) => (isError ? undefined : value);

	const renderStat = (opts: {
		dotClass: 'value' | 'cost' | 'loan';
		label: string;
		caption: string;
		value: number;
		dataTestId: string;
		currency?: CurrencyType;
	}) => (
		<div className="stat" data-testid={opts.dataTestId}>
			<div className="label">
				<span className={`dot ${opts.value ? opts.dotClass : ''}`} />

				<Text c="dark.0" truncate>
					{opts.label}
				</Text>
			</div>

			<div className="value">
				<AmountFormatted
					value={amount(opts.value)}
					currency={opts.currency ?? currency}
					decimalScale={0}
					showZero
				/>
			</div>

			<Text fz={12} c="dark.0" truncate>
				{opts.caption}
			</Text>
		</div>
	);

	return (
		<HoldingsSummaryCardStyles
			data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDINGS_SUMMARY_CARD}
		>
			{renderStat({
				dotClass: 'value',
				label: tMipManage('investors.details.holdings_summary.total_value'),
				caption: tMipManage('investors.details.holdings_summary.total_value_caption'),
				value: totals.totalValue,
				dataTestId: TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_TOTAL_VALUE,
			})}

			{renderStat({
				dotClass: 'cost',
				label: tMipManage('investors.details.holdings_summary.total_cost'),
				caption: tMipManage('investors.details.holdings_summary.total_cost_caption'),
				value: totals.totalCost,
				dataTestId: TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_TOTAL_COST,
			})}

			{showLoan &&
				renderStat({
					dotClass: 'loan',
					label: tMipManage('investors.details.holdings_summary.total_loan_value'),
					caption: tMipManage(
						'investors.details.holdings_summary.total_loan_value_caption'
					),
					value: totals.totalLoanValue,
					dataTestId: TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_TOTAL_LOAN_VALUE,
					currency: loanCurrency,
				})}
		</HoldingsSummaryCardStyles>
	);
};
